// src/pages/assignment/AssignmentModal.jsx (UPDATED - Dynamic Scopes)
import React, { useState, useEffect } from 'react';
import { X, Search, Filter, Package, Building2, MapPin, CheckSquare, Square } from 'lucide-react';
import toast from 'react-hot-toast';
import fieldSupervisorService from '../../services/fieldSupervisorService';

const AssignmentModal = ({ supervisorId, supervisorName, onClose, onSuccess }) => {
  const [assignmentType, setAssignmentType] = useState('SEGMENT');
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Map());

  // Filters
  const [filters, setFilters] = useState({
    projectType: '',
    province: '',
    search: ''
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;

  // ✅ UPDATED: Dynamic scopes based on assignment type
  const SCOPES_SEGMENT = [
    { value: 'SURVEY', label: 'Survey', icon: '🔍', color: 'blue' },
    { value: 'PERMIT', label: 'Permit', icon: '📋', color: 'yellow' },
    { value: 'IMPLEMENTATION', label: 'Implementation', icon: '🏗️', color: 'green' }
  ];

  const SCOPES_SITE = [
    { value: 'SITAC', label: 'SITAC', icon: '📍', color: 'purple' },
    { value: 'CME_IMPLEMENTATION', label: 'CME Implementation', icon: '🏗️', color: 'green' }
  ];

  // ✅ Get current scopes based on assignment type
  const SCOPES = assignmentType === 'SEGMENT' ? SCOPES_SEGMENT : SCOPES_SITE;

  useEffect(() => {
    fetchItems();
  }, [assignmentType, filters, currentPage]);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      let response;
      if (assignmentType === 'SEGMENT') {
        response = await fieldSupervisorService.getAvailableSegments(
          supervisorId,
          {
            projectType: filters.projectType || undefined,
            mainArea: filters.province || undefined,
            search: filters.search || undefined
          },
          currentPage,
          pageSize
        );
      } else {
        response = await fieldSupervisorService.getAvailableSites(
          supervisorId,
          {
            projectType: filters.projectType || undefined,
            provinsi: filters.province || undefined,
            search: filters.search || undefined
          },
          currentPage,
          pageSize
        );
      }

      setItems(response.content || []);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to load items');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssign = async () => {
    if (selectedItems.size === 0) {
      toast.error('Please select at least one item with scopes to assign');
      return;
    }

    // Validate all selected items have at least one scope
    for (const [itemId, scopes] of selectedItems.entries()) {
      if (scopes.size === 0) {
        const item = items.find(i => i.id === itemId);
        const itemName = assignmentType === 'SEGMENT' ? item?.spanId : item?.siteId;
        toast.error(`Please select at least one scope for ${itemName}`);
        return;
      }
    }

    setIsLoading(true);
    try {
      const promises = Array.from(selectedItems.entries()).map(([itemId, scopes]) =>
        fieldSupervisorService.assignToSupervisor({
          supervisorId,
          targetId: itemId,
          type: assignmentType,
          scopes: Array.from(scopes)
        })
      );

      await Promise.all(promises);

      const totalAssignments = selectedItems.size;
      const totalScopes = Array.from(selectedItems.values()).reduce((sum, scopes) => sum + scopes.size, 0);

      toast.success(
        `Successfully assigned ${totalAssignments} ${assignmentType.toLowerCase()}(s) with ${totalScopes} scope(s)!`,
        { duration: 4000 }
      );
      
      setSelectedItems(new Map());
      onSuccess();
    } catch (error) {
      console.error('Error assigning items:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleItemSelection = (itemId) => {
    const newSelection = new Map(selectedItems);
    if (newSelection.has(itemId)) {
      newSelection.delete(itemId);
    } else {
      // Auto-select all available scopes when selecting item
      const item = items.find(i => i.id === itemId);
      const availableScopesArray = Array.isArray(item?.availableScopes) ? item.availableScopes : [];
      
      // ✅ Filter only valid scopes for current type
      const validScopes = availableScopesArray.filter(scope => 
        SCOPES.some(s => s.value === scope)
      );
      
      newSelection.set(itemId, new Set(validScopes));
    }
    setSelectedItems(newSelection);
  };

  const handleToggleScope = (itemId, scope) => {
    const newSelection = new Map(selectedItems);
    const itemScopes = newSelection.get(itemId) || new Set();
    
    if (itemScopes.has(scope)) {
      itemScopes.delete(scope);
    } else {
      itemScopes.add(scope);
    }
    
    if (itemScopes.size === 0) {
      newSelection.delete(itemId);
    } else {
      newSelection.set(itemId, itemScopes);
    }
    
    setSelectedItems(newSelection);
  };

  const handleSelectAllAvailableScopes = (itemId) => {
    const item = items.find(i => i.id === itemId);
    const availableScopesArray = Array.isArray(item?.availableScopes) ? item.availableScopes : [];
    
    // ✅ Filter only valid scopes for current type
    const validScopes = availableScopesArray.filter(scope => 
      SCOPES.some(s => s.value === scope)
    );
    
    const newSelection = new Map(selectedItems);
    newSelection.set(itemId, new Set(validScopes));
    setSelectedItems(newSelection);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(0);
  };

  const handleTypeChange = (type) => {
    setAssignmentType(type);
    setSelectedItems(new Map());
    setCurrentPage(0);
    setFilters({ projectType: '', province: '', search: '' }); // ✅ Reset filters
  };

  const getScopeColor = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200',
      green: 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-200'
    };
    return colors[color] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getSelectedScopeColor = (color) => {
    const colors = {
      blue: 'bg-blue-600 text-white border-blue-700',
      yellow: 'bg-yellow-600 text-white border-yellow-700',
      green: 'bg-green-600 text-white border-green-700',
      purple: 'bg-purple-600 text-white border-purple-700'
    };
    return colors[color] || 'bg-gray-600 text-white border-gray-700';
  };

  // ✅ UPDATED: Get project type options based on assignment type
  const getProjectTypeOptions = () => {
    if (assignmentType === 'SEGMENT') {
      return [
        { value: '', label: 'All Types' },
        { value: 'FO', label: 'Fiber Optic' }
      ];
    } else {
      return [
        { value: '', label: 'All Types' },
        { value: 'TOWER', label: 'Tower' },
        { value: 'TOWER_COLO', label: 'Tower Colocation' }
      ];
    }
  };

  const totalSelectedScopes = Array.from(selectedItems.values())
    .reduce((sum, scopes) => sum + scopes.size, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="p-6 bg-gradient-to-r from-blue-600 to-red-600 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="text-white">
              <h2 className="text-2xl font-bold">Assign to Supervisor</h2>
              <p className="text-blue-100 mt-1">{supervisorName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Type Toggle */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex gap-4">
            <button
              onClick={() => handleTypeChange('SEGMENT')}
              className={`flex-1 py-4 px-6 rounded-lg font-semibold transition-all ${
                assignmentType === 'SEGMENT'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Package className="w-6 h-6 mx-auto mb-2" />
              FO Segments
              <div className="text-xs mt-1 opacity-80">Survey • Permit • Implementation</div>
            </button>
            <button
              onClick={() => handleTypeChange('SITE')}
              className={`flex-1 py-4 px-6 rounded-lg font-semibold transition-all ${
                assignmentType === 'SITE'
                  ? 'bg-red-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Building2 className="w-6 h-6 mx-auto mb-2" />
              Tower Sites
              <div className="text-xs mt-1 opacity-80">SITAC • CME Implementation</div>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Filter className="w-4 h-4 inline mr-1" />
                Project Type
              </label>
              <select
                value={filters.projectType}
                onChange={(e) => handleFilterChange('projectType', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {getProjectTypeOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Province
              </label>
              <input
                type="text"
                value={filters.province}
                onChange={(e) => handleFilterChange('province', e.target.value)}
                placeholder="Enter province name..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Search className="w-4 h-4 inline mr-1" />
                Search
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by name or ID..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Items List */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                {assignmentType === 'SEGMENT' ? <Package className="w-16 h-16 mx-auto" /> : <Building2 className="w-16 h-16 mx-auto" />}
              </div>
              <p className="text-gray-500 text-lg">No items found</p>
              <p className="text-gray-400 text-sm mt-2">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="mb-4 flex items-center justify-between pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-700">
                    {selectedItems.size} item(s) selected
                  </span>
                  <span className="text-gray-500">•</span>
                  <span className="font-semibold text-blue-600">
                    {totalSelectedScopes} scope(s) total
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  Showing {items.length} of {totalElements}
                </span>
              </div>

              {/* Items */}
              <div className="space-y-4">
                {items.map((item) => {
                  const isSelected = selectedItems.has(item.id);
                  const selectedScopes = selectedItems.get(item.id) || new Set();
                  
                  const availableScopesArray = Array.isArray(item.availableScopes) 
                    ? item.availableScopes 
                    : [];
                  const availableScopes = new Set(availableScopesArray);
                  
                  const assignedScopesArray = Array.isArray(item.assignedScopes) 
                    ? item.assignedScopes 
                    : [];
                  const assignedScopes = new Set(assignedScopesArray);
                  
                  const hasAvailableScopes = availableScopes.size > 0;

                  return (
                    <div
                      key={item.id}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isSelected
                          ? assignmentType === 'SEGMENT'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-red-500 bg-red-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (hasAvailableScopes) {
                              handleToggleItemSelection(item.id);
                            } else {
                              toast.info('This item has no available scopes to assign');
                            }
                          }}
                          className="mt-1"
                          disabled={!hasAvailableScopes}
                        >
                          {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Square className={`w-5 h-5 ${hasAvailableScopes ? 'text-gray-400 hover:text-gray-600' : 'text-gray-300'}`} />
                          )}
                        </button>
                        
                        <div className="flex-1">
                          {/* Item Info */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              assignmentType === 'SEGMENT'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {assignmentType === 'SEGMENT' ? item.spanId : item.siteId}
                            </span>
                            
                            {item.isAssignedToCurrentSupervisor && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                ✓ Already Assigned
                              </span>
                            )}
                            
                            {item.hasAnyAssignments && !item.isAssignedToCurrentSupervisor && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                Other supervisors: {item.currentSupervisors.join(', ')}
                              </span>
                            )}
                          </div>

                          <h4 className="font-semibold text-gray-900 mb-2">
                            {assignmentType === 'SEGMENT' ? item.siteNameAB : item.siteName}
                          </h4>

                          {assignedScopes.size > 0 && (
                            <div className="mb-2 text-xs text-gray-600">
                              <span className="font-medium">Currently assigned scopes: </span>
                              {item.assignedScopesString}
                            </div>
                          )}

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-sm text-gray-600">
                            <div>
                              <span className="font-medium">Project:</span> {item.projectName || '-'}
                            </div>
                            <div>
                              <span className="font-medium">Type:</span> {item.projectType || '-'}
                            </div>
                            <div>
                              <span className="font-medium">Location:</span> {
                                assignmentType === 'SEGMENT' ? item.mainArea : item.provinsi
                              }
                            </div>
                            <div>
                              <span className="font-medium">Progress:</span> {item.progressPercentage}%
                            </div>
                          </div>

                          {/* Scope Selection */}
                          {hasAvailableScopes ? (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <label className="text-sm font-semibold text-gray-700">
                                  Select Scopes to Assign:
                                </label>
                                {isSelected && availableScopes.size > 1 && (
                                  <button
                                    onClick={() => handleSelectAllAvailableScopes(item.id)}
                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                  >
                                    Select All Available ({availableScopes.size})
                                  </button>
                                )}
                              </div>
                              
                              <div className="flex flex-wrap gap-2">
                                {SCOPES.map((scope) => {
                                  const isAvailable = availableScopes.has(scope.value);
                                  const isScopeSelected = selectedScopes.has(scope.value);
                                  
                                  if (!isAvailable) {
                                    return (
                                      <span
                                        key={scope.value}
                                        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                                        title="Already assigned to this supervisor"
                                      >
                                        <span>{scope.icon}</span>
                                        <span>{scope.label}</span>
                                        <span className="text-xs">✓</span>
                                      </span>
                                    );
                                  }
                                  
                                  return (
                                    <button
                                      key={scope.value}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleScope(item.id, scope.value);
                                      }}
                                      className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold border transition-all ${
                                        isScopeSelected
                                          ? getSelectedScopeColor(scope.color)
                                          : getScopeColor(scope.color)
                                      }`}
                                    >
                                      <span>{scope.icon}</span>
                                      <span>{scope.label}</span>
                                      {isScopeSelected && <span className="text-xs">✓</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-sm text-amber-800 font-medium">
                                ⚠️ All scopes already assigned to this supervisor
                              </p>
                              <p className="text-xs text-amber-600 mt-1">
                                This {assignmentType.toLowerCase()} is fully assigned.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <div className="flex items-center justify-between">
            <div className="text-sm space-y-1">
              <div className="font-semibold text-gray-700">
                {selectedItems.size} item(s) selected • {totalSelectedScopes} scope(s)
              </div>
              <div className="text-gray-500 text-xs">
                {SCOPES.map(scope => {
                  const count = Array.from(selectedItems.values())
                    .filter(scopes => scopes.has(scope.value))
                    .length;
                  return count > 0 ? `${scope.icon} ${count}` : null;
                }).filter(Boolean).join(' • ')}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={selectedItems.size === 0 || totalSelectedScopes === 0 || isLoading}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-red-600 text-white rounded-lg hover:shadow-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Assigning...' : `Assign (${selectedItems.size})`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentModal;